import React, { useEffect, useState } from 'react';
import type { NotebookFormData, NotebookModalModel } from '../../type/notebook';
import { ModalHeader } from '../../components/modalCommon/modalHeader';
import { ModalBody } from '../../components/modalCommon/modalBody';
import { ModalFooter } from '../../components/modalCommon/modalFooter';
import { BaseModal } from '../../components/modalCommon/modalBase';

const NotebookModal: React.FC<NotebookModalModel> = ({
  show,
  notebook,
  onHide,
  onSave
}) => {
  const [formData, setFormData] = useState<NotebookFormData>({
    name: '',
    note: '',
    isPublic: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (notebook) {
      setFormData({
        name: notebook.name,
        note: notebook.note,
        isPublic: notebook.isPublic
      });
    } else {
      setFormData({
        name: '',
        note: '',
        isPublic: false
      });
    }
  }, [notebook, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      await onSave(formData);
      onHide();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BaseModal open={show} onClose={onHide} size="md">
      <ModalHeader
        title={notebook ? 'Edit Notebook' : 'New Notebook'}
        onClose={onHide}
      />

      <form onSubmit={handleSubmit}>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-control bg-dark text-light border-secondary"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              maxLength={200}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Content *</label>
            <textarea
              className="form-control bg-dark text-light border-secondary"
              rows={8}
              value={formData.note}
              onChange={(e) =>
                setFormData({ ...formData, note: e.target.value })
              }
              disabled={isSubmitting}
            />
          </div>

          <div className="form-check mb-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="notebookPublic"
              checked={formData.isPublic}
              onChange={(e) =>
                setFormData({ ...formData, isPublic: e.target.checked })
              }
              disabled={isSubmitting}
            />
            <label className="form-check-label" htmlFor="notebookPublic">
              Make this notebook public
            </label>
          </div>

          <small className="text-secondary">
            Public notebooks are visible to other users
          </small>
        </ModalBody>

        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !formData.name.trim() || !formData.note.trim()}
          >
            {isSubmitting ? 'Saving…' : notebook ? 'Update' : 'Create'}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default NotebookModal;
